import React, { useMemo } from 'react'
import CellContent from './CellContent'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters: App.OfferListFilters
  selectionId: string
  selections: Array<App.OfferComparisonSelection>
}

function isInclusionInAllOtherOffers(
  inclusion: App.OfferInclusion,
  currentSelectionId: string,
  selections: Array<App.OfferComparisonSelection>,
  bedbankOfferRates: any,
): boolean {
  const otherSelections = selections.filter(s => s.selectionId !== currentSelectionId)

  return otherSelections.every(selection => {
    const otherFilters = selection.params?.filters
    if (!otherFilters) return false

    const otherRateKey = getBedbankRateKey(otherFilters.rooms || [], otherFilters.checkIn || '', otherFilters.checkOut || '')
    const otherRate = bedbankOfferRates[selection.offerId]?.[otherRateKey]?.find((r: any) => r.id === otherFilters.roomRateId)
    const otherInclusions = otherRate?.inclusions || []

    return otherInclusions.some((otherInclusion: App.OfferInclusion) => otherInclusion.description === inclusion.description)
  })
}

function BedbankHotelExclusiveInclusionsCell({
  offer,
  filters,
  selectionId,
  selections,
}: Props) {
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)
  const rateKey = getBedbankRateKey(filters.rooms || [], filters.checkIn || '', filters.checkOut || '')
  const rate = useMemo<App.BedbankRate | undefined>(() => {
    return bedbankOfferRates[offer.id]?.[rateKey]?.find(r => r.id === filters.roomRateId)
  }, [bedbankOfferRates, offer.id, filters.roomRateId, rateKey])

  const [commonInclusions, exclusiveInclusions] = useMemo(() => {
    const inclusions = rate?.inclusions || []
    const common = inclusions.filter(inclusion => isInclusionInAllOtherOffers(inclusion, selectionId, selections, bedbankOfferRates))
    const exclusive = inclusions.filter(inclusion => !isInclusionInAllOtherOffers(inclusion, selectionId, selections, bedbankOfferRates))

    return [common, exclusive]
  }, [rate, selectionId, selections, bedbankOfferRates])

  return <CellContent>
    <VerticalSpacer gap={8}>
      <BodyText variant="medium" weight="bold">Select packages only:</BodyText>
      <Group direction="vertical" gap={4}>
        {exclusiveInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted={false}
        />)}
        {commonInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          colour="neutral-four"
          isHighlighted={false}
        />)}
      </Group>
    </VerticalSpacer>
  </CellContent>
}

export default BedbankHotelExclusiveInclusionsCell
